import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Download as DownloadIcon,
  TableView as TableViewIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import apiService from '../../services/api';

const ExportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [leadStats, setLeadStats] = useState<any>(null);

  // Export filters
  const [filters, setFilters] = useState({
    state: '',
    city: '',
    status: '',
    min_score: '',
    has_email: ''
  });

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const statuses = ['new', 'contacted', 'qualified', 'interested', 'not_interested', 'invalid'];

  useEffect(() => {
    loadLeadStats();
  }, []);

  const loadLeadStats = async () => {
    try {
      const stats = await apiService.getLeadsStats();
      setLeadStats(stats);
    } catch (error) {
      console.error('Failed to load lead stats:', error);
    }
  };

  const handleExportAll = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const blob = await apiService.exportLeadsToExcel();
      downloadBlob(blob, 'all_leads_export.xlsx');
      setSuccess('All leads exported successfully!');
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExportFiltered = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Clean up filters - remove empty values
      const cleanFilters: any = {};
      Object.keys(filters).forEach(key => {
        const value = (filters as any)[key];
        if (value !== undefined && value !== '') {
          cleanFilters[key] = value;
        }
      });

      const blob = await apiService.exportLeadsToExcel(cleanFilters);
      downloadBlob(blob, 'filtered_leads_export.xlsx');
      setSuccess('Filtered leads exported successfully!');
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      city: '',
      status: '',
      min_score: '',
      has_email: ''
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Export Leads to Excel
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Export your leads to Excel format (.xlsx) for easy import into Google Sheets, Excel, or other spreadsheet applications.
      </Typography>

      {/* Lead Statistics */}
      {leadStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <TableViewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Available Leads
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">{leadStats.total_count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">Total Leads</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">{leadStats.new_count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">New Leads</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">{leadStats.with_email_count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">With Email</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">{leadStats.high_score_count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">High Score (80+)</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Quick Export */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <DownloadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Quick Export
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Export all your leads without any filters.
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="All lead data included" secondary="Business details, contact info, scores, etc." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Excel compatible format" secondary="Opens in Excel, Google Sheets, Numbers" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Properly formatted columns" secondary="Optimized column widths and headers" />
                </ListItem>
              </List>

              <Button
                variant="contained"
                onClick={handleExportAll}
                disabled={loading}
                fullWidth
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
              >
                {loading ? 'Exporting...' : 'Export All Leads'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Filtered Export */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Filtered Export
                {getActiveFiltersCount() > 0 && (
                  <Chip
                    label={`${getActiveFiltersCount()} filter${getActiveFiltersCount() > 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Apply filters to export specific leads.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>State</InputLabel>
                    <Select
                      value={filters.state}
                      onChange={(e) => setFilters({...filters, state: e.target.value})}
                      label="State"
                    >
                      <MenuItem value="">All States</MenuItem>
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>{state}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="City"
                    value={filters.city}
                    onChange={(e) => setFilters({...filters, city: e.target.value})}
                    placeholder="e.g., Minneapolis"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                      label="Status"
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min Score"
                    type="number"
                    value={filters.min_score}
                    onChange={(e) => setFilters({...filters, min_score: e.target.value})}
                    inputProps={{ min: 0, max: 100 }}
                    placeholder="e.g., 70"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Email Status</InputLabel>
                    <Select
                      value={filters.has_email}
                      onChange={(e) => setFilters({...filters, has_email: e.target.value})}
                      label="Email Status"
                    >
                      <MenuItem value="">All Leads</MenuItem>
                      <MenuItem value="true">Leads with Email</MenuItem>
                      <MenuItem value="false">Leads without Email</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleExportFiltered}
                  disabled={loading}
                  sx={{ flex: 1 }}
                  startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                >
                  {loading ? 'Exporting...' : 'Export Filtered'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={getActiveFiltersCount() === 0}
                >
                  Clear
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}

      {/* Information */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" paragraph>
            <strong>File Format:</strong> Excel (.xlsx) format compatible with Microsoft Excel, Google Sheets, Apple Numbers, and other spreadsheet applications.
          </Typography>

          <Typography variant="body2" paragraph>
            <strong>Data Included:</strong> Business name, address, contact information, lead scores, status, Google ratings, and all available metadata.
          </Typography>

          <Typography variant="body2" paragraph>
            <strong>Column Layout:</strong> Optimized column widths and professional formatting for easy reading and analysis.
          </Typography>

          <Typography variant="body2">
            <strong>Import to Google Sheets:</strong> Simply upload the .xlsx file to Google Drive and open with Google Sheets. All formatting and data will be preserved.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExportPage;